import { useCallback, useState } from "react";
import { useSettings } from "@/hooks/useSettings";
import { siInputToUS, usInputToSI, type Quantity } from "@/lib/units";

interface UnitInput {
  /** String to show in the <input>. */
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}

/**
 * Adapter for an editable SI field shown in the user's display units.
 *
 * In SI (or with no quantity) it is a pass-through. In US the input shows the
 * converted value; each keystroke is kept as a local draft (so "31 7/" isn't
 * rewritten mid-typing) and immediately converted back to SI for `onChange`,
 * so state, validation and autocorrect still run on SI. On blur the draft is
 * dropped and the field re-renders from the (possibly autocorrected) SI value.
 */
export function useUnitInput(
  value: string,
  quantity: Quantity | undefined,
  onChange?: (v: string) => void,
  onBlur?: () => void,
): UnitInput {
  const { uiUnits } = useSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const active = uiUnits === "US" && !!quantity;

  const change = useCallback((v: string) => {
    if (!active) { onChange?.(v); return; }
    setDraft(v);
    onChange?.(usInputToSI(v, quantity!));
  }, [active, quantity, onChange]);

  const blur = useCallback(() => {
    setDraft(null);
    onBlur?.();
  }, [onBlur]);

  if (!active) return { value, onChange: change, onBlur: blur };
  return { value: draft ?? siInputToUS(value ?? "", quantity!), onChange: change, onBlur: blur };
}
